import { forgotPassword, login, requestOTP, resetPassword, verifySecurityCode } from '@app/api/auth.api';
import {
  ForgotPasswordRequest,
  LoginRequest,
  RequestOTPRequest,
  ResetPasswordRequest,
  SecurityCodePayload,
} from '@app/interfaces/auth';
import { deleteToken, persistToken, readToken } from '@app/services/localStorage.service';
import { setUser, setUserDetails } from '@app/store/slices/userSlice';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export interface AuthSlice {
  token: string | null;
}

const initialState: AuthSlice = {
  token: readToken(),
};

export const doLogin = createAsyncThunk('auth/doLogin', async (loginPayload: LoginRequest, { dispatch }) => {
  const res = await login(loginPayload);
  persistToken(res?.tokens?.access?.token);
  const user = await setUserDetails(res?.user, dispatch);
  return { user, token: res?.tokens?.access?.token };
});

export const doForgotPassword = createAsyncThunk(
  'auth/doForgotPassword',
  async (forgotPassPayload: ForgotPasswordRequest) => forgotPassword(forgotPassPayload),
);

export const doResetPassword = createAsyncThunk(
  'auth/doResetPassword',
  async (resetPassPayload: ResetPasswordRequest) => resetPassword(resetPassPayload),
);

export const doVerifySecurityCode = createAsyncThunk(
  'auth/doVerifySecurityCode',
  async (securityCodePayload: SecurityCodePayload, { dispatch }) => {
    const res = await verifySecurityCode(securityCodePayload);
    setUserDetails(res.user, dispatch);
    persistToken(res.tokens.access.token);
    return res.tokens.access.token;
  },
);

export const doLogout = createAsyncThunk('auth/doLogout', (payload, { dispatch }) => {
  deleteToken();
  dispatch(setUser(null));
  localStorage.clear();
});

export const doRequestOTP = createAsyncThunk('auth/requestOTP', async (requestOTPPayload: RequestOTPRequest) =>
  requestOTP(requestOTPPayload),
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(doLogin.fulfilled, (state, action) => {
      state.token = action.payload.token;
    });
    builder.addCase(doLogout.fulfilled, (state) => {
      state.token = '';
    });
    builder.addCase(doVerifySecurityCode.fulfilled, (state, action) => {
      state.token = action.payload;
    });
  },
});

export default authSlice.reducer;
